import { Icon } from 'Components';
import { Select, Table, Spin } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { PropertyData, SelectOption, TABLE_STATE, UserData } from '../CampaignReport.type';
import UserDetailDrawer from './UserDetailDrawer';
import { useIsMobile } from 'Hooks';
import PaginationElement from 'PhishingManager/components/Pagination';
import { INDIVIDUAL_TABLE_COLUMNS, PROPERTY_TABLE_COLUMNS } from '../CampaignReport.constant';
import PropertyCard from './PropertyCard';
import { ColumnsType } from 'antd/es/table';
import IndividualListItem from './IndividualListItem';
import PropertyListItem from './PropertyListItem';
import DebounceInput from 'PhishingManager/components/DebouncedInput';
import InfinityScrollWrapper from 'Components/InfinityScroll/InfinityScrollWrapper';
import { capitalize } from 'PhishingManager/utils';
import { useTranslation } from 'react-i18next';

// Performed By Options
const performedByOptions: SelectOption[] = [
    { label: "phishingManager.report.table.filters.performedBy.allSources", value: "" },
    { label: "phishingManager.report.table.filters.performedBy.human", value: "Human" },
    { label: "phishingManager.report.table.filters.performedBy.system", value: "System" },
    { label: "phishingManager.report.table.filters.performedBy.unknown", value: "Unknown" },
];

// Result Options
const resultOptions: SelectOption[] = [
    { label: "phishingManager.report.table.filters.result.allResults", value: "" },
    { label: "phishingManager.report.table.filters.result.passed", value: "Passed" },
    { label: "phishingManager.report.table.filters.result.failed", value: "Failed" }
];

// Interaction Options
const interactionOptions: SelectOption[] = [
    { label: "phishingManager.report.table.filters.interaction.allInteractions", value: "" },
    { label: "phishingManager.report.table.filters.interaction.noInteraction", value: "No Interaction" },
    { label: "phishingManager.report.table.filters.interaction.emailSent", value: "Email Sent" },
    { label: "phishingManager.report.table.filters.interaction.emailOpened", value: "Email Opened" },
    { label: "phishingManager.report.table.filters.interaction.clickedLink", value: "Clicked Link" },
    { label: "phishingManager.report.table.filters.interaction.submittedData", value: "Submitted Data" }
];

// Interaction Options
const DELIVERY_STATUS: SelectOption[] = [
    { label: "phishingManager.report.table.filters.deliveryStatus.all", value: "" },
    { label: "phishingManager.report.table.filters.deliveryStatus.delivered", value: "Yes" },
    { label: "phishingManager.report.table.filters.deliveryStatus.undelivered", value: 'No' }
];

const getUniqueFormattedValues = (data: UserData[], key: keyof UserData) => {
    const values: SelectOption[] = [{ label: `phishingManager.report.table.filters.all${key}`, value: "" }];

    data.forEach(item => {
        const itemValue = item[key];
        const arr: string[] = Array.isArray(itemValue) ? itemValue : [];

        arr.forEach(val => {
            const isExist = values.some(v => v.value === val);
            if (!isExist) values.push({ label: capitalize(val), value: val });
        });
    });

    return values;
};

type FILTER_STATE = {
    search: string,
    property: string,
    group: string,
    doneBy: string,
    result: string,
    deliveryStatus: string,
    interaction: string
}

type TableDataType = UserData | PropertyData;
const initDeliveredState = {
    search: "",
    property: "",
    group: "",
    doneBy: "",
    result: "",
    deliveryStatus: "Yes",
    interaction: ""
}

const PAGE_SIZE = 10;
const ReportList = ({ tab, tableState }: { tab: string, tableState: TABLE_STATE }) => {
    const [individualState, setIndividualState] = useState<FILTER_STATE>({ ...initDeliveredState });
    const [selectedData, setSelectedData] = useState<UserData | null>(null);
    const [currentPage, setCurrentPage] = useState(1);
    const isMobile = useIsMobile(951);
    const { t } = useTranslation();
    const isIndividualTab = tab === "individual";
    const { search, property, group, doneBy, interaction, result, deliveryStatus } = individualState;

    const tableColumn: ColumnsType<any> = useMemo(() => {
        return isIndividualTab ? INDIVIDUAL_TABLE_COLUMNS(t) : PROPERTY_TABLE_COLUMNS(t);
    }, [isIndividualTab, t])

    const handleState = (selectKey: string, value: any) => {
        setCurrentPage(1);
        setIndividualState(prev => ({ ...prev, [selectKey]: value }));
    }

    const filteredData: UserData[] = useMemo(() => {
        return tableState.individualData.filter((item) => {
            const searchValue = search?.toLowerCase() || "";
            const name = `${item.first_name || ""} ${item.last_name || ""}`.toLowerCase();
            const email = item.email?.toLowerCase() || "";
    
            const matchesSearch =
                !searchValue ||
                name.includes(searchValue) ||
                email.includes(searchValue);
    
            const matchesProperty = !property || item?.properties?.includes(property);
            const matchesGroup = !group || item?.groups?.includes(group);
            const matchesDoneBy = !doneBy || item.source === doneBy;
            const matchesInteraction = !interaction || item.interaction === interaction;
            const matchesResult = !result || item.result === result;
            const deliveryResult = !deliveryStatus || item.email_delivery === deliveryStatus;
    
            return (
                matchesSearch &&
                matchesProperty &&
                matchesGroup &&
                matchesDoneBy &&
                matchesInteraction &&
                matchesResult &&
                deliveryResult
            );
        });
    }, [search, property, group, doneBy, interaction, result, deliveryStatus, tableState.individualData]);

    const completeData: TableDataType[] = isIndividualTab ? filteredData : tableState.propertyData;
    const tableLoading = isIndividualTab ? tableState.isLoading : tableState.isPropertyLoading;

    const { tableData, tableKey } : { tableData: TableDataType[], tableKey: number } = useMemo(() => {
        const startIdx = PAGE_SIZE * (currentPage - 1);
        return {
            tableKey: Math.random() * currentPage,
            tableData: isMobile ? completeData.slice(0, startIdx + PAGE_SIZE) : completeData.slice(startIdx, startIdx + PAGE_SIZE),
        }
    }, [currentPage, completeData, isMobile]);

    const { propertyOptions, groupOptions } : { propertyOptions: SelectOption[], groupOptions: SelectOption[] } = useMemo(() => {
        return {
            propertyOptions: getUniqueFormattedValues(tableState.individualData, "properties"),
            groupOptions: getUniqueFormattedValues(tableState.individualData, "groups"),
        }
    }, [tableState.individualData])

    useEffect(() => {
        if(tableState.isLoading){
            setCurrentPage(1);
            setIndividualState({...initDeliveredState});
        }
    }, [tableState.isLoading])

    return <Spin spinning={tableLoading}>
        <div className="report-section">
            <div className="table-header">
                <div className='description'>{t(`phishingManager.report.table.header.${tab}`)}</div>
            </div>
            {isIndividualTab ? <div className={`campaign-filter-section`}>
                <DebounceInput debounceSearch={search} placeholder={t('phishingManager.campaignSetup.step1.search')} setDebouncedSearch={(value) => handleState("search", value)} className="campaign-setup-search" />
                <SelectElement value={property} options={propertyOptions.map((item) => ({
                    ...item,
                    label: t(item.label)
                }))} onChange={(value) => handleState("property", value)} />
                <SelectElement value={group} options={groupOptions.map((item) => ({
                    ...item,
                    label: t(item.label)
                }))} onChange={(value) => handleState("group", value)} />
                <SelectElement value={result} options={resultOptions.map((item) => ({
                    ...item,
                    label: t(item.label)
                }))} onChange={(value) => handleState("result", value)} />
                <SelectElement value={doneBy} options={performedByOptions.map((item) => ({
                    ...item,
                    label: t(item.label)
                }))} onChange={(value) => handleState("doneBy", value)} />
                <SelectElement value={interaction} options={interactionOptions.map((item) => ({
                    ...item,
                    label: t(item.label)
                }))} onChange={(value) => handleState("interaction", value)} />
                <SelectElement value={deliveryStatus} options={DELIVERY_STATUS.map((item) => ({
                    ...item,
                    label: t(item.label)
                }))} onChange={(value) => handleState("deliveryStatus", value)} />
            </div> : tableData.length ? <div className='cards-flex'>
                <PropertyCard title={`${tableState.propertyExtraData.averagePassRate}%`} desc={t('phishingManager.report.table.propertyCard.average')} iconType='trendingArrow' />
                <PropertyCard title={`${tableState.propertyExtraData.bestPerforming.name}`} desc={t('phishingManager.report.table.propertyCard.best')} tagValue={tableState.propertyExtraData.bestPerforming.passRate} />
                <PropertyCard title={`${tableState.propertyExtraData.belowPassRate}`} desc={t('phishingManager.report.table.propertyCard.below90')} iconType='phishingManagerWarningModalPrefix' />
            </div> : null}

            {(!isMobile || !tableData.length) ? <div className='table-section'>
                <Table
                    key={tableKey}
                    rowKey={(record) => record.key || record.id}
                    className="report-table"
                    dataSource={tableData}
                    showHeader={tableData.length > 0}
                    columns={tableColumn}
                    pagination={false}
                    showSorterTooltip={false}
                    rowClassName={(_, index) => `${index % 2 === 0 ? "bg-white" : "bg-gray"}${isIndividualTab ? " cursor-pointer" : ""}`}
                    onRow={(record, rowIndex) => {
                        return {
                            onClick: (event) => {
                                if (isIndividualTab) setSelectedData(record);
                            },
                        };
                    }}
                />
                <PaginationElement
                    total={completeData.length}
                    pageSize={PAGE_SIZE}
                    current={currentPage}
                    onChange={setCurrentPage}
                />
            </div> : <div className='list-section'>
                <InfinityScrollWrapper
                    dataLength={tableData.length}
                    hasMore={tableData.length < completeData.length}
                    onLoadMore={() => setTimeout(() => setCurrentPage((prev) => prev + 1), 500)}
                    height={600}
                    endMessageText={t('phishingManager.campaignSetup.step3.property.allItemLoaded')}
                    scrollableId="user-data-scroll-container"
                >
                    {tableData.map((item, idx) => isIndividualTab ?
                        <IndividualListItem t={t} userData={item as UserData} key={idx} /> :
                        <PropertyListItem t={t} propertyData={item as PropertyData} key={idx} />)}
                </InfinityScrollWrapper>
            </div>}
        </div>
        {!!selectedData && <UserDetailDrawer data={selectedData} visible={!!selectedData} onClose={() => setSelectedData(null)} />}
    </Spin>
}

export default ReportList;


const SelectElement = ({ value, options, onChange }: { value: number | string, options: SelectOption[], onChange: (value: any) => void }) => {
    return <Select
        className="campaign-setup-select"
        dropdownClassName="campaign-setup-filter-option-select"
        suffixIcon={<div className="cs-dropdownWrapper">
            <Icon type="campaignSetupDropdown" />
        </div>}
        disabled={options.length <= 1}
        value={value}
        onChange={onChange}
        options={options}
        getPopupContainer={(trigger) => trigger.parentElement}
    />
}